import {
  formatClockTimeAmPm,
  formatGaraazSlotRangeLabel,
  parseGaraazSlotNumber,
} from "../../../components/DeliveryOptions/utils/deliveryTimeUtils";

export type OrderEtaGaraazSlot = {
  minGaraazSlot: number;
  maxGaraazSlot?: number;
  eta?: string;
  minStartTime?: string;
  minEndTime?: string;
  maxStartTime?: string;
  maxEndTime?: string;
};

type OrderEtaEntry = {
  eta?: string;
  minGaraazSlot?: unknown;
  maxGaraazSlot?: unknown;
  minGaraazSlotStartTime?: string;
  minGaraazSlotEndTime?: string;
  maxGaraazSlotStartTime?: string;
  maxGaraazSlotEndTime?: string;
  garaazSlotStartTime?: string;
  garaazSlotEndTime?: string;
};

function clockFromStoredTime(value: string): string {
  const tIndex = value.indexOf("T");
  if (tIndex >= 0 && value.length >= tIndex + 6) {
    return value.slice(tIndex + 1, tIndex + 6);
  }
  return value;
}

export function pickOrderEtaMinGaraazSlot(
  orderEta: unknown,
): OrderEtaGaraazSlot | undefined {
  if (!Array.isArray(orderEta)) {
    return undefined;
  }

  for (let index = orderEta.length - 1; index >= 0; index -= 1) {
    const entry = orderEta[index] as OrderEtaEntry | undefined;
    const minGaraazSlot = parseGaraazSlotNumber(entry?.minGaraazSlot);
    if (minGaraazSlot == null) {
      continue;
    }

    return {
      minGaraazSlot,
      maxGaraazSlot:
        parseGaraazSlotNumber(entry?.maxGaraazSlot) ?? minGaraazSlot,
      eta: entry?.eta,
      minStartTime:
        entry?.minGaraazSlotStartTime ?? entry?.garaazSlotStartTime,
      minEndTime: entry?.minGaraazSlotEndTime ?? entry?.garaazSlotEndTime,
      maxStartTime: entry?.maxGaraazSlotStartTime,
      maxEndTime: entry?.maxGaraazSlotEndTime,
    };
  }

  return undefined;
}

export function formatOrderEtaSlotLabel(
  slot: OrderEtaGaraazSlot,
): string {
  const maxSlot = slot.maxGaraazSlot ?? slot.minGaraazSlot;
  const minIso =
    slot.minStartTime?.trim() ||
    slot.minEndTime?.trim() ||
    slot.eta?.trim() ||
    "";
  const maxIso =
    slot.maxStartTime?.trim() ||
    slot.maxEndTime?.trim() ||
    minIso;

  if (!minIso) {
    if (maxSlot === slot.minGaraazSlot) {
      return `Slot ${slot.minGaraazSlot}`;
    }
    return `Slot ${slot.minGaraazSlot} - Slot ${maxSlot}`;
  }

  return formatGaraazSlotRangeLabel(
    minIso,
    maxIso || minIso,
    slot.minGaraazSlot,
    maxSlot,
  );
}

function formatHoverClock(value: string): string {
  return formatClockTimeAmPm(clockFromStoredTime(value)).replace(":00 ", " ");
}

function formatSlotWindowLine(
  slotId: number,
  startTime?: string,
  endTime?: string,
): string {
  if (!startTime || !endTime) {
    return "";
  }
  return `Slot ${slotId}: ${formatHoverClock(startTime)} to ${formatHoverClock(endTime)}`;
}

export function formatOrderEtaSlotTimings(
  slot: OrderEtaGaraazSlot,
): string {
  const minLine = formatSlotWindowLine(
    slot.minGaraazSlot,
    slot.minStartTime,
    slot.minEndTime,
  );
  const maxSlot = slot.maxGaraazSlot ?? slot.minGaraazSlot;
  const maxLine = formatSlotWindowLine(
    maxSlot,
    slot.maxStartTime,
    slot.maxEndTime,
  );

  if (minLine && maxLine && maxSlot !== slot.minGaraazSlot) {
    return `${minLine}\n${maxLine}`;
  }
  return minLine;
}

export function orderEtaEntryToGaraazSlot(
  entry: OrderEtaEntry | undefined,
): OrderEtaGaraazSlot | undefined {
  return pickOrderEtaMinGaraazSlot(entry ? [entry] : []);
}
